import { useCallback, useEffect, useRef, useState } from "react";
import { createRoot } from "react-dom/client";

import { migrateAnonymousDraft } from "./anonymous-migration";
import {
    clearAnonymousProject,
    readAnonymousProject,
    writeAnonymousProject,
    type AnonymousMigration,
    type AnonymousProjectDraft,
} from "./anonymous-project-store";
import {
    moveStoredStudentShare,
    StudentSharePanel,
    TrainerApp,
} from "./collaboration-ui";
import {
    APP_TITLE,
    DisabledAiAssistant,
    EditorShell,
    PanelHeader,
    StatusGrid,
} from "./editor-shell";
import { brandLogoForLocale } from "./brand-logo";
import { appTitle, brandName, browserLocale, locale, translate as t } from "./locale";
import { importCloudProject } from "./cloud-import";
import {LaunchRedemptionError, redeemLaunch, type LaunchRedemption} from "./launch-api";
import {captureLaunchIntent, clearLaunchTicket, readLaunchTicket} from "./launch-intent";
import { ProjectDeleteDialog } from "./project-delete-dialog";
import { ProjectCount } from "./project-count";
import { ProjectImportDialog } from "./project-import-dialog";
import { loadScratchProject } from "./project-import";
import {hasNewerProjectVersion, responseProjectEtag} from "./project-etag";
import {
    PROJECT_LIMIT_MESSAGE,
    deleteSelectedProjects,
    isProjectLimitReached,
} from "./project-list-actions";
import { ProjectListNavigation, ProjectSelectionActions } from "./project-list-navigation";
import {
    createProjectThumbnail,
    downloadProject,
    loadDefaultProject,
    settleProject,
} from "./project-files";
import { createRandomProjectName } from "./project-name";
import { forgetLastProject, recordProjectOpen, rememberLastProject, resolveInitialProject } from "./project-startup";
import { ScratchEditor, type ScratchVm } from "./scratch-editor";
import {
    authenticatedAccountLabel,
    csrfHeaders,
    readSession,
    type AuthenticatedSession,
    type Session,
} from "./session";
import { isAuthorizationFailure, recoverSessionForOwner } from "./session-recovery";
import { broadcastLogout, subscribeToLogout } from "./session-sync";
import { StudentLoginForm } from "./student-login";
import { initializeTelemetry, registerTelemetrySensitiveValue, trackTelemetryAction, trackTelemetryError } from "./telemetry";
import { TelemetryErrorBoundary } from "./telemetry-error-boundary";
import {
    loginStudentState,
    migrationFailureKind,
    selectStudentView,
    shouldClearAnonymousDraftOnLogout,
    visibleMigrationStatus,
    type MigrationFailureKind,
} from "./student-navigation";
import "./style.css";

document.documentElement.lang = locale;
document.title = appTitle;

const initialLaunchTicket = captureLaunchIntent(
    window.location,
    window.history,
    window.sessionStorage,
    registerTelemetrySensitiveValue,
);

interface Project {
    createdAt: string;
    hasContent: boolean;
    id: string;
    lastOpenedAt: string | null;
    name: string;
    thumbnailUrl: string | null;
    updatedAt: string;
    version: number;
}

const MAX_PROJECT_BYTES = 20 * 1024 * 1024;

const readProject = async (id: string): Promise<{ etag: string; project: Project }> => {
    const response = await fetch(`/api/projects/${id}`);
    if (isAuthorizationFailure(response)) throw new Error("AUTHORIZATION");
    if (!response.ok) throw new Error(t("Nie udało się wczytać projektu"));
    const body = (await response.json()) as { project: Project };
    return { etag: responseProjectEtag(response), project: body.project };
};

const readProjects = async (): Promise<{limit: number; projects: Project[]}> => {
    const response = await fetch("/api/projects");
    if (!response.ok) throw new Error("PROJECT_LIST_FAILED");
    return response.json() as Promise<{limit: number; projects: Project[]}>;
};

const normalizedName = (name: string): string => name.trim() || createRandomProjectName();

const ProjectList = ({
    activeProjectId,
    onBack,
    onOpen,
    onProjectsDeleted,
    projectOpeningDisabled = false,
    session,
}: {
    activeProjectId?: string;
    onBack: () => void;
    onOpen: (projectId: string) => void;
    onProjectsDeleted: (projectIds: string[]) => void;
    projectOpeningDisabled?: boolean;
    session: AuthenticatedSession;
}) => {
    const [projects, setProjects] = useState<Project[]>([]);
    const [projectLimit, setProjectLimit] = useState(50);
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(true);
    const [deleting, setDeleting] = useState(false);
    const [creating, setCreating] = useState(false);
    const creatingRef = useRef(false);
    const [deleteConfirmationVisible, setDeleteConfirmationVisible] = useState(false);

    const finishCreating = () => {
        creatingRef.current = false;
        setCreating(false);
    };

    useEffect(() => {
        void readProjects()
            .then((body) => {
                setProjects(body.projects);
                for (const project of body.projects) registerTelemetrySensitiveValue(project.name);
                setProjectLimit(body.limit);
                trackTelemetryAction("project_list", { result: "success" });
            })
            .catch((caught: unknown) => {
                setError(t("Nie udało się pobrać listy projektów"));
                trackTelemetryAction("project_list", { result: "failure" });
                trackTelemetryError(caught, "handled_operation");
            })
            .finally(() => setLoading(false));
    }, []);

    const createProject = async (checkCurrentList = false) => {
        if (creatingRef.current) return;
        creatingRef.current = true;
        setCreating(true);
        setError("");
        try {
            if (checkCurrentList) {
                const current = await readProjects();
                setProjects(current.projects);
                setProjectLimit(current.limit);
                for (const project of current.projects) registerTelemetrySensitiveValue(project.name);
                if (current.projects.length) {
                    onOpen(current.projects[0].id);
                    return;
                }
                if (current.limit < 1) {
                    setError(PROJECT_LIMIT_MESSAGE);
                    return;
                }
            }
            const response = await fetch("/api/projects", {
                method: "POST",
                headers: { "Content-Type": "application/json", ...csrfHeaders(session) },
                body: JSON.stringify({ name: createRandomProjectName() }),
            });
            if (await isProjectLimitReached(response)) {
                trackTelemetryAction("project_create", { reason: "limit", result: "rejected" });
                return setError(PROJECT_LIMIT_MESSAGE);
            }
            if (!response.ok) {
                trackTelemetryAction("project_create", { result: "failure" });
                return setError(t("Nie udało się utworzyć projektu"));
            }
            const body = (await response.json()) as { project: Project };
            trackTelemetryAction("project_create", { result: "success" });
            onOpen(body.project.id);
        } catch (caught) {
            trackTelemetryAction("project_create", { result: "failure" });
            trackTelemetryError(caught, "handled_operation");
            setError(checkCurrentList ? t("Nie udało się sprawdzić lub utworzyć projektu") : t("Nie udało się utworzyć projektu"));
        } finally {
            finishCreating();
        }
    };

    const toggleProject = (projectId: string) => {
        setSelectedIds((current) => {
            const next = new Set(current);
            if (next.has(projectId)) next.delete(projectId);
            else next.add(projectId);
            return next;
        });
    };

    const removeSelected = async () => {
        if (!selectedIds.size) return;
        setDeleting(true);
        setError("");
        const result = await deleteSelectedProjects({
            csrfToken: session.csrfToken,
            onNetworkError: (error) => trackTelemetryError(error, "handled_operation"),
            projects,
            selectedIds,
        });
        if (result.deletedIds.length) {
            setProjects((current) => current.filter((project) => !result.deletedIds.includes(project.id)));
            onProjectsDeleted(result.deletedIds);
            trackTelemetryAction("project_delete", { result: "success", source: "list" });
        }
        setSelectedIds(new Set(result.failedIds));
        if (result.failedIds.length) {
            trackTelemetryAction("project_delete", { result: "failure", source: "list" });
            setError(t("Nie udało się usunąć {value0} zaznaczonych projektów. Odświeżono listę.", { value0: result.failedIds.length }));
        }
        try {
            const body = await readProjects();
            setProjects(body.projects);
            setProjectLimit(body.limit);
        } catch (caught) {
            trackTelemetryAction("project_list", { result: "failure" });
            trackTelemetryError(caught, "handled_operation");
            if (!result.failedIds.length) setError(t("Projekty usunięto, ale nie udało się odświeżyć listy"));
        } finally {
            setDeleting(false);
        }
    };

    const storeFull = projects.length >= projectLimit;
    const returnToEditor = () => {
        if (!projectOpeningDisabled) {
            const targetProjectId = activeProjectId && projects.some((project) => project.id === activeProjectId) ?
                activeProjectId : projects[0]?.id;
            if (targetProjectId) return onOpen(targetProjectId);
            void createProject(true);
            return;
        }
        onBack();
    };
    return (
        <main className="projects-page">
            <img
                alt={brandName}
                className={`projects-brand-logo${locale === "pl" ? "" : " projects-brand-logo-en"}`}
                src={brandLogoForLocale(locale)}
            />
            <div className="projects-heading">
                <h1>{t("MOJE PROJEKTY")}</h1>
                <p>{authenticatedAccountLabel(session)}</p>
                {!loading && <ProjectCount count={projects.length} limit={projectLimit} />}
            </div>
            <div className="projects-toolbar">
                <ProjectListNavigation
                    backDisabled={loading || deleting || creating}
                    createDisabled={loading || deleting || creating || storeFull}
                    createOnBack={!projectOpeningDisabled && !loading && projects.length === 0}
                    onBack={returnToEditor}
                    onCreate={() => void createProject()}
                />
                {!loading && projects.length > 0 && (
                    <ProjectSelectionActions
                        deleting={deleting}
                        onClear={() => setSelectedIds(new Set())}
                        onDelete={() => setDeleteConfirmationVisible(true)}
                        selectedCount={selectedIds.size}
                    />
                )}
            </div>
            {error && <p className="form-error" role="alert">{error}</p>}
            {storeFull && <p className="project-limit-message" role="status">{PROJECT_LIMIT_MESSAGE}</p>}
            {loading ? (
                <p>{t("Ładowanie…")}</p>
            ) : (
                <div className="project-grid">
                    {projects.map((project) => (
                        <article
                            className={`project-list-card${selectedIds.has(project.id) ? " is-selected" : ""}`}
                            key={project.id}
                        >
                            <input
                                aria-label={t("Zaznacz projekt {name}", { name: project.name })}
                                checked={selectedIds.has(project.id)}
                                className="project-selection-checkbox"
                                disabled={deleting}
                                onChange={() => toggleProject(project.id)}
                                type="checkbox"
                            />
                            <button
                                className="project-open-button"
                                disabled={projectOpeningDisabled}
                                onClick={() => {
                                    trackTelemetryAction("project_open", { source: "list" });
                                    onOpen(project.id);
                                }}
                                title={projectOpeningDisabled ? t("Najpierw usuń projekt, aby dokończyć migrację") : undefined}
                                type="button"
                            >
                                <span className="project-thumbnail">
                                    {project.thumbnailUrl ? (
                                        <img alt={t("Miniatura projektu {name}", { name: project.name })} src={project.thumbnailUrl} />
                                    ) : (
                                        <span>Scratch</span>
                                    )}
                                </span>
                                <strong>{project.name}</strong>
                                <small>{new Date(project.updatedAt).toLocaleString(browserLocale)}</small>
                                <span className="project-open-label">{t("Otwórz")}</span>
                            </button>
                        </article>
                    ))}
                    {!projects.length && <p>{t("Nie masz jeszcze projektów. Utwórz pierwszy!")}</p>}
                </div>
            )}
            {deleteConfirmationVisible && (
                <ProjectDeleteDialog
                    count={selectedIds.size}
                    onCancel={() => setDeleteConfirmationVisible(false)}
                    onConfirm={async () => {
                        await removeSelected();
                        setDeleteConfirmationVisible(false);
                    }}
                    working={deleting}
                />
            )}
        </main>
    );
};

const AnonymousEditor = ({
    initialStarter,
    initialSession,
    onAuthenticated,
    onLogout,
    onMigrated,
    onStarterLoaded,
    onShowProjects,
}: {
    initialStarter?: {content: Blob; name: string};
    initialSession?: AuthenticatedSession;
    onAuthenticated: (session: AuthenticatedSession) => void;
    onLogout: () => Promise<void>;
    onMigrated: (session: AuthenticatedSession, projectId: string) => void;
    onStarterLoaded?: () => void;
    onShowProjects: () => void;
}) => {
    const [name, setName] = useState(createRandomProjectName);
    const [status, setStatus] = useState(t("Wczytywanie…"));
    const [dirty, setDirty] = useState(false);
    const [changeSerial, setChangeSerial] = useState(0);
    const [saving, setSaving] = useState(false);
    const [localSaveGeneration, setLocalSaveGeneration] = useState(0);
    const [editorReady, setEditorReady] = useState(false);
    const [editorVm, setEditorVm] = useState<ScratchVm>();
    const [importVisible, setImportVisible] = useState(false);
    const migrationSession = initialSession;
    const [migrationWorking, setMigrationWorking] = useState(false);
    const [migrationFailure, setMigrationFailure] = useState<MigrationFailureKind>();
    const vmRef = useRef<ScratchVm>();
    const nameRef = useRef(name);
    const [draftId, setDraftId] = useState<string>(() => crypto.randomUUID());
    const draftIdRef = useRef(draftId);
    const migrationRef = useRef<AnonymousMigration>();
    const suppressChanges = useRef(true);
    const savingRef = useRef(false);
    const changeListenerRef = useRef<() => void>();
    const automaticMigrationRef = useRef("");
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [shareNoticeTarget, setShareNoticeTarget] = useState<HTMLDivElement | null>(null);

    const updateSaving = (value: boolean) => {
        savingRef.current = value;
        setSaving(value);
    };

    const updateName = (value: string) => {
        nameRef.current = value;
        setName(value);
        setDirty(true);
        setChangeSerial((current) => current + 1);
        setStatus(t("Niezapisane zmiany"));
    };

    const saveLocal = useCallback(async (trigger = "manual"): Promise<AnonymousProjectDraft | undefined> => {
        const vm = vmRef.current;
        if (!vm || savingRef.current) return undefined;
        updateSaving(true);
        setStatus(t("Zapisywanie lokalnie…"));
        try {
            const nextName = normalizedName(nameRef.current);
            if (nextName !== nameRef.current) {
                nameRef.current = nextName;
                setName(nextName);
            }
            const draft: AnonymousProjectDraft = {
                content: await vm.saveProjectSb3(),
                id: draftIdRef.current,
                migration: migrationRef.current,
                name: nextName,
                thumbnail: await createProjectThumbnail(vm),
                updatedAt: Date.now(),
            };
            await writeAnonymousProject(draft);
            setDirty(false);
            setStatus(t("Zapisano lokalnie"));
            setLocalSaveGeneration((value) => value + 1);
            registerTelemetrySensitiveValue(nextName);
            trackTelemetryAction("project_save", { mode: "local", result: "success", trigger });
            return draft;
        } catch (caught) {
            setStatus(t("Nie udało się zapisać lokalnie"));
            trackTelemetryAction("project_save", { mode: "local", result: "failure", trigger });
            trackTelemetryError(caught, "handled_operation");
            return undefined;
        } finally {
            updateSaving(false);
        }
    }, []);

    const initializeVm = useCallback(async (vm: ScratchVm) => {
        vmRef.current = vm;
        setEditorVm(vm);
        changeListenerRef.current = () => {
            if (suppressChanges.current) return;
            setDirty(true);
            setChangeSerial((current) => current + 1);
            setStatus(t("Niezapisane zmiany"));
        };
        vm.on("PROJECT_CHANGED", changeListenerRef.current);
        try {
            const draft = await readAnonymousProject();
            suppressChanges.current = true;
            if (initialStarter) {
                registerTelemetrySensitiveValue(initialStarter.name);
                nameRef.current = initialStarter.name;
                setName(initialStarter.name);
                await vm.loadProject(await initialStarter.content.arrayBuffer());
                await settleProject();
                await clearAnonymousProject();
                const nextDraftId = crypto.randomUUID();
                draftIdRef.current = nextDraftId;
                setDraftId(nextDraftId);
                migrationRef.current = undefined;
            } else if (draft) {
                registerTelemetrySensitiveValue(draft.name);
                draftIdRef.current = draft.id;
                setDraftId(draft.id);
                migrationRef.current = draft.migration;
                nameRef.current = draft.name;
                setName(draft.name);
                await vm.loadProject(await draft.content.arrayBuffer());
                await settleProject();
            } else {
                await loadDefaultProject(vm);
            }
            suppressChanges.current = false;
            setDirty(false);
            setStatus(initialStarter ? t("Wczytano projekt startowy") : draft ? t("Odtworzono projekt lokalny") : t("Nowy projekt"));
            setEditorReady(true);
            if (initialStarter || !draft) await saveLocal("auto");
            if (initialStarter) onStarterLoaded?.();
        } catch (error) {
            suppressChanges.current = false;
            console.error("AnonymousEditor.initializeVm failed", error);
            setStatus(t("Nie udało się otworzyć projektu lokalnego"));
            trackTelemetryError(error, "handled_operation");
        }
    }, [initialStarter, onStarterLoaded, saveLocal]);

    const onVmInit = useCallback((vm: ScratchVm) => {
        void initializeVm(vm);
    }, [initializeVm]);

    useEffect(
        () => () => {
            if (vmRef.current && changeListenerRef.current)
                vmRef.current.removeListener("PROJECT_CHANGED", changeListenerRef.current);
        },
        [],
    );

    useEffect(() => {
        if (!editorReady || !dirty || saving || migrationWorking) return;
        const timer = window.setTimeout(() => void saveLocal("auto"), 5_000);
        return () => window.clearTimeout(timer);
    }, [changeSerial, dirty, editorReady, migrationWorking, saveLocal, saving]);

    const migrate = useCallback(async (session: AuthenticatedSession) => {
        if (!vmRef.current || migrationWorking) return;
        setMigrationWorking(true);
        setMigrationFailure(undefined);
        setStatus(t("Dodawanie projektu do konta…"));
        try {
            const draft = await saveLocal();
            if (!draft) throw new Error("LOCAL_SAVE_FAILED");
            const migratedProjectId = await migrateAnonymousDraft(draft, session.csrfToken, {
                writeDraft: async (nextDraft) => {
                    migrationRef.current = nextDraft.migration;
                    await writeAnonymousProject(nextDraft);
                },
            });
            trackTelemetryAction("project_create", { result: "success", source: "migration" });
            moveStoredStudentShare(draft.id, migratedProjectId);
            onMigrated(session, migratedProjectId);
        } catch (error) {
            const failure = migrationFailureKind(error);
            setMigrationFailure(failure);
            setStatus(
                failure === "project-limit"
                    ? PROJECT_LIMIT_MESSAGE
                    : t("Nie udało się dodać projektu do konta"),
            );
            trackTelemetryAction("project_create", {
                reason: failure === "project-limit" ? "limit" : "migration",
                result: failure === "project-limit" ? "rejected" : "failure",
                source: "migration",
            });
        } finally {
            setMigrationWorking(false);
        }
    }, [migrationWorking, onMigrated, saveLocal]);

    useEffect(() => {
        if (!editorReady || !migrationSession) return;
        const key = `${migrationSession.user.id}:${draftIdRef.current}`;
        if (automaticMigrationRef.current === key) return;
        automaticMigrationRef.current = key;
        void migrate(migrationSession);
    }, [editorReady, migrate, migrationSession]);

    const createNew = async () => {
        if (!vmRef.current || !window.confirm(t("Nowy projekt zastąpi bieżącą zawartość. Kontynuować?"))) return;
        try {
            suppressChanges.current = true;
            await loadDefaultProject(vmRef.current);
            const nextDraftId = crypto.randomUUID();
            draftIdRef.current = nextDraftId;
            setDraftId(nextDraftId);
            migrationRef.current = undefined;
            const nextName = createRandomProjectName();
            nameRef.current = nextName;
            setName(nextName);
            suppressChanges.current = false;
            setDirty(true);
            setChangeSerial((current) => current + 1);
            setStatus(t("Utworzono nowy projekt"));
            const saved = await saveLocal();
            trackTelemetryAction("project_create", {
                mode: "local",
                result: saved ? "success" : "failure",
            });
        } catch (caught) {
            suppressChanges.current = false;
            setStatus(t("Nie udało się utworzyć projektu"));
            trackTelemetryAction("project_create", { mode: "local", result: "failure" });
            trackTelemetryError(caught, "handled_operation");
        }
    };

    const loadFile = async (file: File) => {
        if (!vmRef.current || !file.name.toLowerCase().endsWith(".sb3") || file.size === 0 || file.size > MAX_PROJECT_BYTES) {
            setStatus(t("Wybierz prawidłowy plik .sb3 do 20 MiB"));
            trackTelemetryAction("project_import", { reason: "invalid_file", result: "rejected", source: "file" });
            return;
        }
        if (!window.confirm(t("Wczytanie projektu zastąpi bieżącą zawartość. Kontynuować?"))) return;
        try {
            registerTelemetrySensitiveValue(file.name);
            suppressChanges.current = true;
            await vmRef.current.loadProject(await file.arrayBuffer());
            await settleProject();
            suppressChanges.current = false;
            setDirty(true);
            setChangeSerial((current) => current + 1);
            setStatus(t("Wczytano projekt — oczekiwanie na autosave"));
            trackTelemetryAction("project_import", { result: "success", source: "file" });
        } catch (caught) {
            suppressChanges.current = false;
            setStatus(t("Nie udało się wczytać projektu"));
            trackTelemetryAction("project_import", { result: "failure", source: "file" });
            trackTelemetryError(caught, "handled_operation");
        }
    };

    const loadLink = async (url: string) => {
        if (!vmRef.current) throw new Error(t("Edytor Scratch nie jest jeszcze gotowy"));
        if (!window.confirm(t("Wczytanie projektu zastąpi bieżącą zawartość. Kontynuować?")))
            return false;
        try {
            const imported = await loadScratchProject(url);
            registerTelemetrySensitiveValue(imported.title);
            suppressChanges.current = true;
            await vmRef.current.loadProject(JSON.stringify(imported.project));
            await settleProject();
            nameRef.current = imported.title;
            setName(imported.title);
            suppressChanges.current = false;
            setDirty(true);
            setChangeSerial((current) => current + 1);
            setStatus(t("Wczytano projekt z linku — oczekiwanie na autosave"));
            return true;
        } catch (error) {
            suppressChanges.current = false;
            setStatus(t("Nie udało się wczytać projektu z linku"));
            throw error;
        }
    };

    const applyPulledProject = async (payload: {
        name?: string;
        project: ArrayBuffer;
    }) => {
        if (!vmRef.current) throw new Error("EDITOR_UNAVAILABLE");
        suppressChanges.current = true;
        await vmRef.current.loadProject(payload.project);
        await settleProject();
        suppressChanges.current = false;
        if (payload.name) {
            registerTelemetrySensitiveValue(payload.name);
            nameRef.current = payload.name;
            setName(payload.name);
        }
        setDirty(true);
        setChangeSerial((current) => current + 1);
        setStatus(t("Zmiany z Pull oczekują na zapis lokalny"));
    };

    const displayedStatus = visibleMigrationStatus(status, migrationFailure, PROJECT_LIMIT_MESSAGE);
    const panel = (
        <>
            <PanelHeader
                subtitle={migrationSession ? authenticatedAccountLabel(migrationSession) : t("Niezalogowany")}
                title={APP_TITLE}
            />
            <StatusGrid items={[
                { label: t("Edytor"), value: editorReady ? t("gotowy") : t("ładowanie…"), state: editorReady ? "ready" : "loading" },
            ]} />
            <div className="trainer-update-notice-slot" ref={setShareNoticeTarget} />
            <section className="project-card share-card" aria-labelledby="project-title">
                <h2 id="project-title">{t("Projekt")}</h2>
                <label className="project-name-field">
                    <span>{t("Nazwa projektu:")}</span>
                    <input
                        aria-label={t("Nazwa projektu")}
                        disabled={migrationWorking}
                        maxLength={100}
                        onChange={(event) => updateName(event.target.value)}
                        value={name}
                    />
                </label>
                <div className="project-primary-actions">
                    <button className="project-new-button" disabled={!editorReady || migrationWorking} onClick={() => void createNew()} type="button">{t("nowy")}</button>
                    <button className="project-save-button" disabled={!editorReady || saving || migrationWorking} onClick={() => void saveLocal()} type="button">{t("zapisz")}</button>
                </div>
                <div className="project-file-actions">
                    <button
                        aria-label={t("Pobierz .sb3")}
                        className="project-download-button"
                        disabled={!editorReady || migrationWorking}
                        onClick={() => vmRef.current && void downloadProject(vmRef.current, nameRef.current)}
                        type="button"
                    >
                        {t("pobierz")}
                    </button>
                    <button
                        aria-label={t("Wczytaj projekt")}
                        className="project-import-button"
                        disabled={!editorReady || migrationWorking}
                        onClick={() => setImportVisible(true)}
                        type="button"
                    >
                        {t("wczytaj")}
                    </button>
                </div>
                <input
                    accept=".sb3"
                    hidden
                    onChange={(event) => {
                        const file = event.target.files?.[0];
                        event.target.value = "";
                        if (file) void loadFile(file);
                    }}
                    ref={fileInputRef}
                    type="file"
                />
                <p
                    className="action-status"
                    data-state={displayedStatus === PROJECT_LIMIT_MESSAGE ? "error" : undefined}
                    role="status"
                >
                    {displayedStatus}
                </p>
                <StudentSharePanel
                    applyPulledProject={applyPulledProject}
                    cloudSaveGeneration={localSaveGeneration}
                    ensureSaved={async () => Boolean(await saveLocal())}
                    key={draftId}
                    noticeTarget={shareNoticeTarget}
                    projectId={draftId}
                    projectName={name}
                    vm={editorVm}
                />
            </section>
            <DisabledAiAssistant />
            <section className="application-card account-actions" aria-label={t("Konto GIGA-SCRATCH")}>
                {!migrationSession ? (
                    <>
                        <p className="panel-copy">{t("Projekt jest bezpiecznie zapisywany w tej przeglądarce.")}</p>
                        <StudentLoginForm disabled={!editorReady} onLogin={onAuthenticated} />
                    </>
                ) : (
                    <>
                        <p className="panel-copy">{t("Projekt zostanie dodany do konta {name}.", { name: migrationSession.user.name })}</p>
                        {migrationFailure === "project-limit" && (
                            <>
                                <button disabled={migrationWorking} onClick={onShowProjects} type="button">{t("Moje projekty")}</button>
                                <button
                                    className="secondary-button"
                                    disabled={migrationWorking}
                                    onClick={() => void onLogout()}
                                    type="button"
                                >
                                    {t("Wyloguj")}
                                </button>
                                <button
                                    className="danger-button"
                                    disabled
                                    title={t("Projekt lokalny nie został jeszcze dodany do konta")}
                                    type="button"
                                >
                                    {t("Usuń projekt")}
                                </button>
                            </>
                        )}
                        {migrationFailure === "other" && (
                            <button disabled={migrationWorking} onClick={() => void migrate(migrationSession)} type="button">
                                {t("Ponów migrację")}
                            </button>
                        )}
                    </>
                )}
            </section>
        </>
    );

    return (
        <>
            <EditorShell collapsible panel={panel}>
                <ScratchEditor onVmInit={onVmInit} />
            </EditorShell>
            {importVisible && (
                <ProjectImportDialog
                    onCancel={() => setImportVisible(false)}
                    onChooseFile={() => {
                        setImportVisible(false);
                        fileInputRef.current?.click();
                    }}
                    onLoadLink={async (url) => {
                        const loaded = await loadLink(url);
                        if (loaded) setImportVisible(false);
                        return loaded;
                    }}
                />
            )}
        </>
    );
};

const CloudProjectEditor = ({
    onDeleted,
    onLogout,
    onOpen,
    onProjectLoaded,
    onSessionRefreshed,
    onShowProjects,
    projectId,
    session,
}: {
    onDeleted: () => void;
    onLogout: () => Promise<void>;
    onOpen: (id: string) => void;
    onProjectLoaded?: () => void;
    onSessionRefreshed: (session: AuthenticatedSession) => void;
    onShowProjects: () => void;
    projectId: string;
    session: AuthenticatedSession;
}) => {
    const [project, setProject] = useState<Project>();
    const [etag, setEtag] = useState("");
    const [name, setName] = useState("");
    const [status, setStatus] = useState(t("Wczytywanie…"));
    const [changeSerial, setChangeSerial] = useState(0);
    const [dirty, setDirty] = useState(false);
    const [conflict, setConflict] = useState(false);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [editorVm, setEditorVm] = useState<ScratchVm>();
    const [cloudSaveGeneration, setCloudSaveGeneration] = useState(0);
    const [importVisible, setImportVisible] = useState(false);
    const [importing, setImporting] = useState(false);
    const [deleteConfirmationVisible, setDeleteConfirmationVisible] = useState(false);
    const [authorizationPaused, setAuthorizationPaused] = useState(false);
    const vmRef = useRef<ScratchVm>();
    const nameRef = useRef("");
    const projectRef = useRef<Project>();
    const etagRef = useRef("");
    const sessionRef = useRef(session);
    const suppressChanges = useRef(false);
    const savingRef = useRef(false);
    const authorizationPausedRef = useRef(false);
    const recoveryInProgressRef = useRef(false);
    const recoveryRetryPendingRef = useRef(false);
    const recoveryRetryUsedRef = useRef(false);
    const importingRef = useRef(false);
    const changeListenerRef = useRef<() => void>();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [shareNoticeTarget, setShareNoticeTarget] = useState<HTMLDivElement | null>(null);

    const updateSaving = (value: boolean) => {
        savingRef.current = value;
        setSaving(value);
    };

    useEffect(() => {
        sessionRef.current = session;
    }, [session]);

    const updateProject = (nextProject: Project) => {
        projectRef.current = nextProject;
        setProject(nextProject);
    };

    const updateEtag = (nextEtag: string) => {
        etagRef.current = nextEtag;
        setEtag(nextEtag);
    };

    const pauseForExpiredSession = useCallback(() => {
        authorizationPausedRef.current = true;
        setAuthorizationPaused(true);
        if (!recoveryRetryUsedRef.current) recoveryRetryPendingRef.current = true;
        setStatus(t("Sesja wygasła. Zaloguj się ponownie na konto właściciela i wróć do tej karty."));
    }, []);

    const versionCheckPaused = useCallback(
        () => savingRef.current || authorizationPausedRef.current,
        [],
    );

    const checkProjectVersion = useCallback(async () => {
        if (!projectRef.current || !etagRef.current || versionCheckPaused()) return;
        try {
            const current = await readProject(projectId);
            if (versionCheckPaused()) return;
            if (hasNewerProjectVersion(etagRef.current, current.project.version)) {
                setConflict(true);
                setStatus(t("Projekt zmienił się w innej karcie"));
            }
        } catch (error) {
            if (error instanceof Error && error.message === "AUTHORIZATION")
                pauseForExpiredSession();
        }
    }, [pauseForExpiredSession, projectId, versionCheckPaused]);

    useEffect(() => {
        const check = () => void checkProjectVersion();
        const onVisibilityChange = () => {
            if (document.visibilityState === "visible") check();
        };
        const interval = window.setInterval(check, 3_000);
        window.addEventListener("focus", check);
        document.addEventListener("visibilitychange", onVisibilityChange);
        return () => {
            window.clearInterval(interval);
            window.removeEventListener("focus", check);
            document.removeEventListener("visibilitychange", onVisibilityChange);
        };
    }, [checkProjectVersion]);

    const loadFromServer = useCallback(async (vm?: ScratchVm) => {
        const activeVm = vm ?? vmRef.current;
        if (!activeVm) return;
        setStatus(t("Wczytywanie…"));
        try {
            const result = await readProject(projectId);
            updateProject(result.project);
            registerTelemetrySensitiveValue(result.project.name);
            nameRef.current = result.project.name;
            setName(result.project.name);
            updateEtag(result.etag);
            suppressChanges.current = true;
            if (result.project.hasContent) {
                const response = await fetch(`/api/projects/${projectId}/content`);
                if (isAuthorizationFailure(response)) throw new Error("AUTHORIZATION");
                if (!response.ok) throw new Error();
                await activeVm.loadProject(await response.arrayBuffer());
                await settleProject();
            } else await loadDefaultProject(activeVm);
            suppressChanges.current = false;
            setDirty(false);
            setConflict(false);
            setStatus(t("Zapisano"));
            onProjectLoaded?.();
            void recordProjectOpen(projectId, sessionRef.current.csrfToken)
                .catch((caught: unknown) => trackTelemetryError(caught, "handled_operation"));
            trackTelemetryAction("project_open", { result: "success", source: "editor" });
        } catch (caught) {
            suppressChanges.current = false;
            if (caught instanceof Error && caught.message === "AUTHORIZATION") pauseForExpiredSession();
            else setStatus(t("Nie udało się wczytać projektu"));
            trackTelemetryAction("project_open", { result: "failure", source: "editor" });
            trackTelemetryError(caught, "handled_operation");
        }
    }, [onProjectLoaded, pauseForExpiredSession, projectId]);

    const onVmInit = useCallback((vm: ScratchVm) => {
        vmRef.current = vm;
        setEditorVm(vm);
        changeListenerRef.current = () => {
            if (suppressChanges.current) return;
            setDirty(true);
            setChangeSerial((value) => value + 1);
            setStatus(t("Niezapisane zmiany"));
        };
        vm.on("PROJECT_CHANGED", changeListenerRef.current);
        void loadFromServer(vm);
    }, [loadFromServer]);

    useEffect(
        () => () => {
            if (vmRef.current && changeListenerRef.current)
                vmRef.current.removeListener("PROJECT_CHANGED", changeListenerRef.current);
        },
        [],
    );

    const save = useCallback(async (trigger = "manual") => {
        const vm = vmRef.current;
        const activeProject = projectRef.current;
        if (
            !vm ||
            !activeProject ||
            !etagRef.current ||
            savingRef.current ||
            conflict ||
            authorizationPausedRef.current
            || importingRef.current
        ) return false;
        updateSaving(true);
        setStatus(t("Zapisywanie…"));
        let currentEtag = etagRef.current;
        const nextName = normalizedName(nameRef.current);
        try {
            if (nextName !== nameRef.current) {
                nameRef.current = nextName;
                setName(nextName);
            }
            let currentProject = activeProject;
            if (nextName !== currentProject.name) {
                const response = await fetch(`/api/projects/${projectId}`, {
                    method: "PATCH",
                    headers: {
                        "Content-Type": "application/json",
                        "If-Match": currentEtag,
                        ...csrfHeaders(sessionRef.current),
                    },
                    body: JSON.stringify({ name: nextName }),
                });
                if (isAuthorizationFailure(response)) throw new Error("AUTHORIZATION");
                if (response.status === 412) throw new Error("CONFLICT");
                if (!response.ok) throw new Error("SAVE");
                currentEtag = responseProjectEtag(response);
                currentProject = {
                    ...currentProject,
                    name: nextName,
                    version: Number(currentEtag.slice(1, -1)),
                };
                updateProject(currentProject);
                updateEtag(currentEtag);
            }
            const contentResponse = await fetch(`/api/projects/${projectId}/content`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/x.scratch.sb3",
                    "If-Match": currentEtag,
                    ...csrfHeaders(sessionRef.current),
                },
                body: await vm.saveProjectSb3(),
            });
            if (isAuthorizationFailure(contentResponse)) throw new Error("AUTHORIZATION");
            if (contentResponse.status === 412) throw new Error("CONFLICT");
            if (!contentResponse.ok) throw new Error("SAVE");
            currentEtag = responseProjectEtag(contentResponse);
            updateEtag(currentEtag);
            const thumbnail = await createProjectThumbnail(vm);
            if (thumbnail) {
                const thumbnailResponse = await fetch(`/api/projects/${projectId}/thumbnail`, {
                    method: "PUT",
                    headers: {
                        "Content-Type": "image/png",
                        "If-Match": currentEtag,
                        ...csrfHeaders(sessionRef.current),
                    },
                    body: thumbnail,
                });
                if (isAuthorizationFailure(thumbnailResponse)) throw new Error("AUTHORIZATION");
                if (thumbnailResponse.status === 412) throw new Error("CONFLICT");
                if (!thumbnailResponse.ok) throw new Error("SAVE");
                currentEtag = responseProjectEtag(thumbnailResponse);
                updateEtag(currentEtag);
            }
            updateProject({
                ...currentProject,
                name: nextName,
                hasContent: true,
                version: Number(currentEtag.slice(1, -1)),
            });
            setDirty(false);
            setStatus(t("Zapisano"));
            recoveryRetryPendingRef.current = false;
            recoveryRetryUsedRef.current = false;
            setCloudSaveGeneration((value) => value + 1);
            registerTelemetrySensitiveValue(nextName);
            trackTelemetryAction("project_save", { mode: "cloud", result: "success", trigger });
            return true;
        } catch (error) {
            if (error instanceof Error && error.message === "AUTHORIZATION") {
                pauseForExpiredSession();
            } else if (error instanceof Error && error.message === "CONFLICT") {
                setConflict(true);
                setStatus(t("Projekt zmienił się na innym komputerze"));
                trackTelemetryAction("collaboration_conflict", { source: "project_save" });
            } else setStatus(t("Błąd zapisu — spróbuj ponownie"));
            trackTelemetryAction("project_save", { mode: "cloud", result: "failure", trigger });
            if (!(error instanceof Error && ["AUTHORIZATION", "CONFLICT", "SAVE"].includes(error.message)))
                trackTelemetryError(error, "handled_operation");
            return false;
        } finally {
            updateSaving(false);
        }
    }, [conflict, pauseForExpiredSession, projectId]);

    const recoverSession = useCallback(async () => {
        if (!authorizationPausedRef.current || recoveryInProgressRef.current) return;
        recoveryInProgressRef.current = true;
        try {
            const result = await recoverSessionForOwner(session.user.id, readSession);
            if (result.kind === "different-account") {
                setStatus(
                    t("Zalogowano inne konto. Wróć do konta właściciela projektu albo pobierz projekt jako .sb3."),
                );
                return;
            }
            if (result.kind === "logged-out") {
                setStatus(
                    t("Zaloguj się na konto właściciela projektu, wróć do tej karty albo pobierz projekt jako .sb3."),
                );
                return;
            }
            sessionRef.current = result.session;
            onSessionRefreshed(result.session);
            // eslint-disable-next-line require-atomic-updates -- The in-progress guard serializes recovery attempts.
            authorizationPausedRef.current = false;
            setAuthorizationPaused(false);
            if (recoveryRetryPendingRef.current && !recoveryRetryUsedRef.current) {
                recoveryRetryPendingRef.current = false;
                recoveryRetryUsedRef.current = true;
                setStatus(t("Sesja odzyskana — ponawianie zapisu…"));
                await save();
            }
        } catch (caught) {
            setStatus(t("Nie udało się odnowić sesji. Spróbuj ponownie po powrocie do tej karty."));
            trackTelemetryError(caught, "handled_operation");
        } finally {
            // eslint-disable-next-line require-atomic-updates -- This invocation owns the in-progress guard.
            recoveryInProgressRef.current = false;
        }
    }, [onSessionRefreshed, save, session.user.id]);

    const checkSession = useCallback(async () => {
        if (authorizationPausedRef.current) return recoverSession();
        try {
            const result = await recoverSessionForOwner(session.user.id, readSession);
            if (result.kind !== "recovered") {
                pauseForExpiredSession();
                return;
            }
            sessionRef.current = result.session;
            onSessionRefreshed(result.session);
        } catch (caught) {
            trackTelemetryError(caught, "handled_operation");
        }
    }, [onSessionRefreshed, pauseForExpiredSession, recoverSession, session.user.id]);

    const checkShareAuthorization = useCallback(() => {
        void checkSession();
    }, [checkSession]);

    useEffect(() => {
        const onFocus = () => void checkSession();
        const onVisibilityChange = () => {
            if (document.visibilityState === "visible") void checkSession();
        };
        const unsubscribe = subscribeToLogout(() => {
            pauseForExpiredSession();
            void checkSession();
        });
        window.addEventListener("focus", onFocus);
        document.addEventListener("visibilitychange", onVisibilityChange);
        return () => {
            unsubscribe();
            window.removeEventListener("focus", onFocus);
            document.removeEventListener("visibilitychange", onVisibilityChange);
        };
    }, [checkSession, pauseForExpiredSession]);

    useEffect(() => {
        if (!dirty || saving || importing || conflict || authorizationPaused) return;
        const timer = window.setTimeout(() => void save("auto"), 5_000);
        return () => window.clearTimeout(timer);
    }, [authorizationPaused, changeSerial, conflict, dirty, importing, save, saving]);

    const applyPulledProject = async (payload: { name?: string; project: ArrayBuffer }) => {
        const vm = vmRef.current;
        if (!vm) throw new Error("EDITOR_UNAVAILABLE");
        suppressChanges.current = true;
        await vm.loadProject(payload.project);
        await settleProject();
        suppressChanges.current = false;
        if (payload.name) {
            registerTelemetrySensitiveValue(payload.name);
            nameRef.current = payload.name;
            setName(payload.name);
        }
        setDirty(true);
        setChangeSerial((value) => value + 1);
        setStatus(t("Zmiany z Pull oczekują na autosave"));
    };

    const saveAsCopy = async () => {
        const vm = vmRef.current;
        if (!vm) return;
        setSaving(true);
        try {
            const created = await fetch("/api/projects", {
                method: "POST",
                headers: { "Content-Type": "application/json", ...csrfHeaders(session) },
                body: JSON.stringify({ name: `${normalizedName(nameRef.current)} — kopia`.slice(0, 100) }),
            });
            if (await isProjectLimitReached(created)) {
                setStatus(PROJECT_LIMIT_MESSAGE);
                trackTelemetryAction("project_create", { reason: "limit", result: "rejected", source: "copy" });
                setSaving(false);
                return;
            }
            if (!created.ok) throw new Error();
            const body = (await created.json()) as { project: Project };
            const saved = await fetch(`/api/projects/${body.project.id}/content`, {
                method: "PUT",
                headers: { "Content-Type": "application/x.scratch.sb3", "If-Match": responseProjectEtag(created), ...csrfHeaders(session) },
                body: await vm.saveProjectSb3(),
            });
            if (!saved.ok) throw new Error();
            trackTelemetryAction("project_create", { result: "success", source: "copy" });
            onOpen(body.project.id);
        } catch (caught) {
            setStatus(t("Nie udało się zapisać kopii"));
            trackTelemetryAction("project_create", { result: "failure", source: "copy" });
            trackTelemetryError(caught, "handled_operation");
            setSaving(false);
        }
    };

    const deleteProject = async () => {
        if (!etag) return;
        setDeleting(true);
        try {
            const response = await fetch(`/api/projects/${projectId}`, {
                method: "DELETE",
                headers: { "If-Match": etag, ...csrfHeaders(session) },
            });
            if (response.ok) {
                trackTelemetryAction("project_delete", { result: "success", source: "editor" });
                onDeleted();
            } else {
                setStatus(t("Nie udało się usunąć projektu"));
                trackTelemetryAction("project_delete", { result: "failure", source: "editor" });
            }
        } catch (caught) {
            setStatus(t("Nie udało się usunąć projektu"));
            trackTelemetryAction("project_delete", { result: "failure", source: "editor" });
            trackTelemetryError(caught, "handled_operation");
        } finally {
            setDeleting(false);
        }
    };

    const createNew = async () => {
        if (dirty && !(await save())) return;
        try {
            const response = await fetch("/api/projects", {
                method: "POST",
                headers: { "Content-Type": "application/json", ...csrfHeaders(session) },
                body: JSON.stringify({ name: createRandomProjectName() }),
            });
            if (await isProjectLimitReached(response)) {
                trackTelemetryAction("project_create", { reason: "limit", result: "rejected" });
                return setStatus(PROJECT_LIMIT_MESSAGE);
            }
            if (!response.ok) {
                trackTelemetryAction("project_create", { result: "failure" });
                return setStatus(t("Nie udało się utworzyć projektu"));
            }
            const body = (await response.json()) as { project: Project };
            trackTelemetryAction("project_create", { result: "success" });
            onOpen(body.project.id);
        } catch (caught) {
            trackTelemetryAction("project_create", { result: "failure" });
            trackTelemetryError(caught, "handled_operation");
            setStatus(t("Nie udało się utworzyć projektu"));
        }
    };

    const importAsNew = async (data: ArrayBuffer | string, sourceName: string) => {
        const vm = vmRef.current;
        if (!vm || importingRef.current || authorizationPausedRef.current) return false;
        if (dirty && !(await save())) return false;
        // eslint-disable-next-line require-atomic-updates -- Synchronous guard claims this editor's import operation.
        importingRef.current = true;
        setImporting(true);
        updateSaving(true);
        let previous: ArrayBuffer | undefined;
        try {
            previous = await (await vm.saveProjectSb3()).arrayBuffer();
            suppressChanges.current = true;
            await vm.loadProject(data);
            await settleProject();
            const importedId = await importCloudProject(await vm.saveProjectSb3(), sourceName, sessionRef.current);
            trackTelemetryAction("project_import", { result: "success", source: typeof data === "string" ? "link" : "file" });
            onOpen(importedId);
            return true;
        } catch (caught) {
            if (previous) {
                try {
                    await vm.loadProject(previous);
                    await settleProject();
                } catch (restoreError) {
                    trackTelemetryError(restoreError, "handled_operation");
                }
            }
            if (caught instanceof Error && caught.message === "PROJECT_LIMIT_REACHED")
                setStatus(PROJECT_LIMIT_MESSAGE);
            else if (caught instanceof Error && caught.message === "AUTHORIZATION")
                pauseForExpiredSession();
            else setStatus(t("Nie udało się zaimportować projektu. Bieżący projekt pozostał zapisany."));
            trackTelemetryAction("project_import", { result: "failure", source: typeof data === "string" ? "link" : "file" });
            trackTelemetryError(caught, "handled_operation");
            return false;
        } finally {
            suppressChanges.current = false;
            // eslint-disable-next-line require-atomic-updates -- This invocation owns the import guard.
            importingRef.current = false;
            setImporting(false);
            updateSaving(false);
        }
    };

    const loadFile = async (file: File) => {
        if (!vmRef.current || !file.name.toLowerCase().endsWith(".sb3") || file.size === 0 || file.size > MAX_PROJECT_BYTES) {
            setStatus(t("Wybierz prawidłowy plik .sb3 do 20 MiB"));
            trackTelemetryAction("project_import", { reason: "invalid_file", result: "rejected", source: "file" });
            return;
        }
        try {
            registerTelemetrySensitiveValue(file.name);
            await importAsNew(await file.arrayBuffer(), file.name);
        } catch (caught) {
            setStatus(t("Nie udało się wczytać projektu"));
            trackTelemetryAction("project_import", { result: "failure", source: "file" });
            trackTelemetryError(caught, "handled_operation");
        }
    };

    const loadLink = async (url: string) => {
        if (!vmRef.current) throw new Error(t("Edytor Scratch nie jest jeszcze gotowy"));
        try {
            const imported = await loadScratchProject(url);
            registerTelemetrySensitiveValue(imported.title);
            if (await importAsNew(JSON.stringify(imported.project), imported.title)) return true;
            throw new Error(t("Nie udało się zapisać nowego projektu"));
        } catch (error) {
            setStatus(t("Nie udało się wczytać projektu z linku"));
            throw error;
        }
    };

    const showProjectList = async () => {
        if (dirty && !(await save())) return;
        onShowProjects();
    };
    const logout = async () => {
        if (dirty && !(await save())) return;
        await onLogout();
    };

    const panel = (
        <>
            <PanelHeader subtitle={authenticatedAccountLabel(session)} title={APP_TITLE} />
            <StatusGrid items={[
                { label: t("Edytor"), value: editorVm ? t("gotowy") : t("ładowanie…"), state: editorVm ? "ready" : "loading" },
            ]} />
            <div className="trainer-update-notice-slot" ref={setShareNoticeTarget} />
            {conflict && (
                <section className="project-card share-update" role="alert">
                    <p>{t("Wersja na serwerze jest nowsza")}</p>
                    <button disabled={authorizationPaused} onClick={() => void loadFromServer()} type="button">{t("Wczytaj wersję z serwera")}</button>
                    <button disabled={authorizationPaused} onClick={() => void saveAsCopy()} type="button">{t("Zapisz jako kopię")}</button>
                </section>
            )}
            <section className="project-card share-card" aria-labelledby="project-title">
                <h2 id="project-title">{t("Projekt")}</h2>
                <label className="project-name-field">
                    <span>{t("Nazwa projektu:")}</span>
                    <input
                        aria-label={t("Nazwa projektu")}
                        disabled={!project || saving || authorizationPaused}
                        maxLength={100}
                        onChange={(event) => {
                            nameRef.current = event.target.value;
                            setName(event.target.value);
                            setDirty(true);
                            setChangeSerial((value) => value + 1);
                            setStatus(t("Niezapisane zmiany"));
                        }}
                        value={name}
                    />
                </label>
                <div className="project-primary-actions">
                    <button className="project-new-button" disabled={!project || saving || authorizationPaused} onClick={() => void createNew()} type="button">{t("nowy")}</button>
                    <button className="project-save-button" disabled={!project || saving || conflict || authorizationPaused} onClick={() => void save()} type="button">{t("zapisz")}</button>
                </div>
                <div className="project-file-actions">
                    <button
                        aria-label={t("Pobierz .sb3")}
                        className="project-download-button"
                        disabled={!editorVm || saving}
                        onClick={() => vmRef.current && void downloadProject(vmRef.current, nameRef.current)}
                        type="button"
                    >
                        {t("pobierz")}
                    </button>
                    <button
                        aria-label={t("Wczytaj projekt")}
                        className="project-import-button"
                        disabled={!project || saving || authorizationPaused}
                        onClick={() => setImportVisible(true)}
                        type="button"
                    >
                        {t("wczytaj")}
                    </button>
                </div>
                <input
                    accept=".sb3"
                    hidden
                    onChange={(event) => {
                        const file = event.target.files?.[0];
                        event.target.value = "";
                        if (file) void loadFile(file);
                    }}
                    ref={fileInputRef}
                    type="file"
                />
                <p
                    className="action-status"
                    data-state={status === PROJECT_LIMIT_MESSAGE ? "error" : undefined}
                    role="status"
                >
                    {status}
                </p>
                {!authorizationPaused && <StudentSharePanel
                    applyPulledProject={applyPulledProject}
                    cloudSaveGeneration={cloudSaveGeneration}
                    ensureSaved={save}
                    onAuthorizationFailure={checkShareAuthorization}
                    noticeTarget={shareNoticeTarget}
                    projectId={projectId}
                    projectName={name}
                    remoteProjectId={projectId}
                    session={session}
                    vm={editorVm}
                />}
            </section>
            <DisabledAiAssistant />
            <section className="application-card account-actions">
                {authorizationPaused && (
                    <StudentLoginForm
                        disabled={false}
                        formLabel="Ponowne logowanie do projektu"
                        onLogin={(nextSession) => {
                            if (nextSession.user.id !== session.user.id) {
                                setStatus(t("Zalogowano inne konto. Wróć do konta właściciela albo pobierz projekt jako .sb3."));
                                return;
                            }
                            void recoverSession();
                        }}
                    />
                )}
                <button disabled={authorizationPaused} onClick={() => void showProjectList()} type="button">{t("Moje projekty")}</button>
                <button className="secondary-button" disabled={authorizationPaused} onClick={() => void logout()} type="button">{t("Wyloguj")}</button>
                <button
                    className="danger-button"
                    disabled={!project || saving || deleting || authorizationPaused}
                    onClick={() => setDeleteConfirmationVisible(true)}
                    type="button"
                >
                    {t("Usuń projekt")}
                </button>
            </section>
        </>
    );

    return (
        <>
            <EditorShell collapsible panel={panel}>
                <ScratchEditor onVmInit={onVmInit} />
            </EditorShell>
            {importVisible && (
                <ProjectImportDialog
                    onCancel={() => setImportVisible(false)}
                    onChooseFile={() => {
                        setImportVisible(false);
                        fileInputRef.current?.click();
                    }}
                    onLoadLink={async (url) => {
                        const loaded = await loadLink(url);
                        if (loaded) setImportVisible(false);
                        return loaded;
                    }}
                />
            )}
            {deleteConfirmationVisible && (
                <ProjectDeleteDialog
                    count={1}
                    onCancel={() => setDeleteConfirmationVisible(false)}
                    onConfirm={deleteProject}
                    working={deleting}
                />
            )}
        </>
    );
};

const StudentApp = ({launchTicket}: {launchTicket?: string}) => {
    const [session, setSession] = useState<Session>();
    const [startupError, setStartupError] = useState("");
    const [projectId, setProjectId] = useState<string>();
    const [lastProjectId, setLastProjectId] = useState<string>();
    const [showProjects, setShowProjects] = useState(false);
    const [pendingGuestMigration, setPendingGuestMigration] = useState(false);
    const [freshAnonymous, setFreshAnonymous] = useState(0);
    const [anonymousStarter, setAnonymousStarter] = useState<{content: Blob; name: string}>();
    const [launchRetryMessage, setLaunchRetryMessage] = useState("");
    const clearDraftAfterLaunchLoadRef = useRef(false);
    const launchProjectLoadPendingRef = useRef(false);

    const applyLaunch = useCallback(async (
        ticket: string,
        previousSession: Session,
        draft: AnonymousProjectDraft | undefined,
    ): Promise<void> => {
        const result: LaunchRedemption = await redeemLaunch(ticket);
        if (result.kind === "anonymous") {
            setAnonymousStarter({content: result.content, name: result.name});
            setSession({authenticated: false});
            setPendingGuestMigration(false);
            setShowProjects(false);
            return;
        }
        const changedAccount = !previousSession.authenticated || previousSession.user.id !== result.session.user.id;
        setSession(result.session);
        if (changedAccount) broadcastLogout();
        if (result.kind === "project") {
            clearDraftAfterLaunchLoadRef.current = Boolean(draft);
            launchProjectLoadPendingRef.current = true;
            setLaunchRetryMessage("");
            setPendingGuestMigration(false);
            setProjectId(result.projectId);
            setLastProjectId(result.projectId);
            rememberLastProject(result.session.user.id, result.projectId);
            setShowProjects(false);
            return;
        }
        if (result.kind === "project-limit") {
            setLaunchRetryMessage(PROJECT_LIMIT_MESSAGE);
            setPendingGuestMigration(false);
            setShowProjects(true);
            return;
        }
        if (result.kind === "starter-error") {
            setLaunchRetryMessage(t("Nie udało się utworzyć projektu startowego. Logowanie zostało zachowane."));
            setPendingGuestMigration(false);
            setShowProjects(true);
            return;
        }
        clearLaunchTicket();
        setLaunchRetryMessage("");
        if (!draft) {
            const initialProjectId = await resolveInitialProject({
                createProjectName: createRandomProjectName,
                csrfToken: result.session.csrfToken,
                onProjectCreated: () => trackTelemetryAction("project_create", {result: "success"}),
                userId: result.session.user.id,
            });
            setProjectId(initialProjectId);
            setLastProjectId(initialProjectId);
        }
        setPendingGuestMigration(Boolean(draft));
        setShowProjects(false);
    }, []);

    const retryLaunch = useCallback(async () => {
        const ticket = readLaunchTicket();
        if (!ticket || !session) return;
        setStartupError("");
        try {
            await applyLaunch(ticket, session, await readAnonymousProject());
        } catch (caught) {
            setStartupError(t("Nie udało się ponowić wczytania projektu startowego"));
            trackTelemetryError(caught, "handled_operation");
        }
    }, [applyLaunch, session]);
    const handleStarterLoaded = useCallback(() => clearLaunchTicket(), []);
    const handleLaunchProjectLoaded = useCallback(() => {
        if (!launchProjectLoadPendingRef.current) return;
        launchProjectLoadPendingRef.current = false;
        const clearDraft = clearDraftAfterLaunchLoadRef.current;
        clearDraftAfterLaunchLoadRef.current = false;
        clearLaunchTicket();
        if (clearDraft) {
            void clearAnonymousProject().catch((caught) => trackTelemetryError(caught, "handled_operation"));
        }
    }, []);

    useEffect(() => {
        void Promise.all([readSession(), readAnonymousProject()])
            .then(async ([currentSession, draft]) => {
                if (launchTicket) {
                    await applyLaunch(launchTicket, currentSession, draft);
                    return;
                }
                if (currentSession.authenticated && !draft) {
                    const initialProjectId = await resolveInitialProject({
                        createProjectName: createRandomProjectName,
                        csrfToken: currentSession.csrfToken,
                        onProjectCreated: () => trackTelemetryAction("project_create", {
                            result: "success",
                        }),
                        userId: currentSession.user.id,
                    });
                    setProjectId(initialProjectId);
                    setLastProjectId(initialProjectId);
                }
                setSession(currentSession);
                setPendingGuestMigration(currentSession.authenticated && Boolean(draft));
                setShowProjects(false);
            })
            .catch((caught) => {
                const launchFailure = caught instanceof LaunchRedemptionError;
                if (launchFailure) clearLaunchTicket();
                setStartupError(launchFailure
                    ? t("Link uruchomieniowy jest nieprawidłowy, wygasł albo został już użyty")
                    : t("Serwer Giga-Scratch jest niedostępny"));
                if (caught instanceof Error && caught.message === "PROJECT_CREATE_FAILED")
                    trackTelemetryAction("project_create", { result: "failure" });
                if (caught instanceof Error && caught.message === "PROJECT_LIST_FAILED")
                    trackTelemetryAction("project_list", { result: "failure" });
                trackTelemetryError(caught, "handled_operation");
            });
    }, [applyLaunch, launchTicket]);

    const openProject = (id: string, authenticatedSession?: AuthenticatedSession) => {
        const owner = authenticatedSession ?? (session?.authenticated ? session : undefined);
        if (owner) rememberLastProject(owner.user.id, id);
        setProjectId(id);
        setLastProjectId(id);
        setShowProjects(false);
    };

    const logout = async () => {
        if (!session?.authenticated) return;
        if (shouldClearAnonymousDraftOnLogout(pendingGuestMigration)) {
            try {
                await clearAnonymousProject();
            } catch (caught) {
                trackTelemetryAction("logout", { reason: "draft_cleanup", result: "failure" });
                trackTelemetryError(caught, "handled_operation");
                return;
            }
        }
        try {
            const response = await fetch("/api/auth/session", {
                method: "DELETE",
                headers: csrfHeaders(session),
            });
            if (!response.ok) {
                trackTelemetryAction("logout", {
                    accountType: session.user.accountType,
                    result: "failure",
                });
                return;
            }
            trackTelemetryAction("logout", {
                accountType: session.user.accountType,
                result: "success",
            });
        } catch (caught) {
            trackTelemetryAction("logout", {
                accountType: session.user.accountType,
                result: "failure",
            });
            trackTelemetryError(caught, "handled_operation");
            return;
        }
        setProjectId(undefined);
        setLastProjectId(undefined);
        setPendingGuestMigration(false);
        setShowProjects(false);
        setSession({ authenticated: false });
        try {
            broadcastLogout();
        } catch (caught) {
            trackTelemetryError(caught, "handled_operation");
        }
        setFreshAnonymous((value) => value + 1);
    };

    if (startupError) return <main className="startup-error"><p className="form-error">{startupError}</p></main>;
    if (!session) return <main className="startup-error"><p>{t("Ładowanie…")}</p></main>;

    const studentView = selectStudentView({
        authenticated: session.authenticated,
        hasProject: Boolean(projectId),
        pendingGuestMigration,
        showProjects,
    });

    if (studentView === "anonymous")
        return (
            <AnonymousEditor
                initialStarter={anonymousStarter}
                initialSession={session.authenticated ? session : undefined}
                key={`anonymous-${freshAnonymous}`}
                onAuthenticated={(authenticatedSession) => {
                    const nextState = loginStudentState(authenticatedSession);
                    setSession(nextState.session);
                    setPendingGuestMigration(nextState.pendingGuestMigration);
                }}
                onLogout={logout}
                onMigrated={(authenticatedSession, migratedProjectId) => {
                    setSession(authenticatedSession);
                    setPendingGuestMigration(false);
                    openProject(migratedProjectId, authenticatedSession);
                }}
                onStarterLoaded={handleStarterLoaded}
                onShowProjects={() => setShowProjects(true)}
            />
        );

    if (studentView === "projects" && session.authenticated)
        return (
            <>
                {launchRetryMessage && (
                    <aside className="launch-error" role="alert">
                        <strong>{launchRetryMessage}</strong>
                        <p>{t("Usuń niepotrzebny projekt, a następnie ponów wczytanie projektu startowego.")}</p>
                        <button onClick={() => void retryLaunch()} type="button">{t("Ponów starter")}</button>
                    </aside>
                )}
                <ProjectList
                    activeProjectId={pendingGuestMigration ? undefined : lastProjectId}
                    onBack={() => setShowProjects(false)}
                    onOpen={openProject}
                    onProjectsDeleted={(deletedProjectIds) => {
                        if (pendingGuestMigration && deletedProjectIds.length) {
                            setShowProjects(false);
                            setFreshAnonymous((value) => value + 1);
                            return;
                        }
                        if (!lastProjectId || !deletedProjectIds.includes(lastProjectId)) return;
                        forgetLastProject(session.user.id);
                        setProjectId(undefined);
                        setLastProjectId(undefined);
                    }}
                    projectOpeningDisabled={pendingGuestMigration}
                    session={session}
                />
            </>
        );

    if (!session.authenticated || !projectId)
        return <main className="startup-error"><p className="form-error">{t("Nie udało się otworzyć projektu")}</p></main>;

    return (
        <CloudProjectEditor
            key={projectId}
            onDeleted={() => {
                setProjectId(undefined);
                setLastProjectId(undefined);
                setShowProjects(true);
            }}
            onLogout={logout}
            onOpen={openProject}
            onProjectLoaded={handleLaunchProjectLoaded}
            onSessionRefreshed={setSession}
            onShowProjects={() => setShowProjects(true)}
            projectId={projectId}
            session={session}
        />
    );
};

const App = () => {
    const trainerMatch = /^\/s\/([0-9a-f-]{36})$/u.exec(window.location.pathname);
    return trainerMatch?.[1] ? <TrainerApp sessionId={trainerMatch[1]} /> : <StudentApp launchTicket={initialLaunchTicket} />;
};

const appTarget = document.querySelector<HTMLElement>("#app");
if (!appTarget) throw new Error("Scratch CG application mount point is missing");
void initializeTelemetry().catch(() => undefined);
createRoot(appTarget).render(<TelemetryErrorBoundary><App /></TelemetryErrorBoundary>);
